/**
 * A job a person started — the Ocado fill, a sync, a skill run now — as a box
 * in the thread (2026-10-12): its name, how it stands, and one line for the
 * step it is on. It stays, shown as complete, until `/clear`. The chat is not
 * held by it, and the agent is told nothing of it.
 *
 * × dismisses it (v2.01): one that runs is stopped, and the box goes.
 */
import { useState } from "react";

import { api } from "../api/client";
import { ok, reason } from "../api/query";
import type { Job } from "./fold";
import styles from "./JobBox.module.css";
import { useChat } from "./store";

const STATE: Record<Job["state"], string> = {
  running: "Running",
  done: "Complete",
  failed: "Failed",
};

export function JobBox({ job }: { job: Job }) {
  const [failed, setFailed] = useState<string | null>(null);
  const dismiss = () => {
    setFailed(null);
    ok(api.POST("/api/chat/jobs/{job_id}/dismiss", { params: { path: { job_id: job.id } } }))
      .then((answer) => useChat.getState().job(answer))
      .catch((e: unknown) => setFailed(reason(e)));
  };
  return (
    <div
      className={`${styles.box} ${styles[job.state]}`}
      role="status"
      aria-label={`${job.title}: ${STATE[job.state]}`}
    >
      <div className={styles.head}>
        {job.state === "running" && <span className={styles.spinner} aria-hidden="true" />}
        <span className={styles.title}>{job.title}</span>
        <span className={styles.state}>{STATE[job.state]}</span>
        <button
          className={styles.dismiss}
          onClick={dismiss}
          aria-label={job.state === "running" ? `Stop ${job.title}` : `Dismiss ${job.title}`}
          title={job.state === "running" ? "Stop it, and take the box away" : "Take the box away"}
        >
          ×
        </button>
      </div>
      {job.step && <div className={styles.step}>{job.step}</div>}
      {job.error && <div className={styles.error}>{job.error}</div>}
      {failed && (
        <div className={styles.error} role="alert">
          {failed}
        </div>
      )}
    </div>
  );
}
